import { useState } from "react";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@apollo/client";
import sha256 from "crypto-js/sha256";
import enc from "crypto-js/enc-hex";
import { useTranslation } from 'react-i18next';

// material
import { LoadingButton } from "@mui/lab";
import {
  Stack,
  IconButton,
  InputAdornment,
} from "@mui/material";

// graphql
import { AdminGQLFunction } from "../../graphql/ADMIN";
// components
import Iconify from "../../components/Iconify";
import {
  FormProvider,
  RHFTextField,
} from "../../components/hook-form";

import PHONE_REG_EXP from '../../components/hook-form/regrex';

export default function CreateAdminForm() {
  const { t } = useTranslation();
  const [createadmin] = useMutation(AdminGQLFunction.CREATE_ADMIN);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  const CreateAdminSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('login.email_valid'))
      .required(t('people.email_req')),
    password: Yup.string().required(t('login.password_req')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], t('login.password_match'))
      .required(t('people.confirm_password_req')),
    username: Yup.string().required(t('people.username_req')),
    telephone: Yup.string()
      .matches(PHONE_REG_EXP, t('people.phone_invalid'))
      .nullable(true),
  });

  const defaultValues = {
    email: "",
    password: "",
    confirmPassword: "",
    username: "",
    telephone: "",
  };

  const methods = useForm({
    resolver: yupResolver(CreateAdminSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
  } = methods;

  const onSubmit = async (input) => {
    const { data } = await createadmin({
      variables: {
        email: input.email,
        password: sha256(input.password).toString(enc.Hex),
        username: input.username,
        telephone: input.telephone,
      },
    });
    const info = data.createNewAdmin;
    if (info.err) setError(info.err, { type: "custom", message: info.msg });
    else {
      navigate(`/admin/info/${info.id}`);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <RHFTextField name="email" label={t('common.email')} autoComplete="off" />
        <RHFTextField name="username" label={t('common.username')} autoComplete="off"/>
        <RHFTextField name="telephone" label={t('common.telephone')} autoComplete="off" />
        <RHFTextField
          name="password"
          label={t('people.temp_password')}
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <RHFTextField
          name="confirmPassword"
          label={t('people.confirm_password')}
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {t('people.create_btn', { identity: t('nav.admin') })}
      </LoadingButton>
    </FormProvider>
  );
}
